// Wiki：灵智原生的知识增强整理出来的概念、实体、资料摘要。左边按类型和分类浏览，上面搜索；
// 读一页时，正文里的 [[链接]] 是站内跳转，右边是「关联」（链出去的、链到它的）和来源资料。

import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { useInfiniteQuery, useQueries, useQuery } from '@tanstack/react-query'
import ReactMarkdown, { type Components } from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { ArrowUpRight, FileText, Search } from 'lucide-react'
import { getKnowledge, getKnowledgeBase } from '../../api/kb'
import { slugPath, WIKI_TYPE_LABEL, wikiFolders, wikiIndex, wikiLinks, wikiPage, wikiPages, wikiSearch, wikiStats, type WikiFolder, type WikiPage } from '../../api/wiki'
import { Pill } from '../../ui'
import '../chat/chat.css'
import { FolderRow, KbHead } from '../kb/pages'
import { useDocPreview } from '../kb/usePreview'
import '../kb/kb.css'
import './wiki.css'

const TYPES = ['concept', 'entity', 'summary'] as const
const order = (t: string) => { const i = (TYPES as readonly string[]).indexOf(t); return i < 0 ? 99 : i }
const PAGE = 40

export function WikiPageRoute() {
  const { kbId = '', '*': rest = '' } = useParams()
  const slug = decodeURIComponent(rest).replace(/^\/+|\/+$/g, '')
  const kb = useQuery({ queryKey: ['knowledge-base', kbId], queryFn: () => getKnowledgeBase(kbId) })
  const stats = useQuery({ queryKey: ['wiki-stats', kbId], queryFn: () => wikiStats(kbId), staleTime: 5 * 60_000 })
  const [type, setType] = useState<string | undefined>(undefined)
  const [folder, setFolder] = useState<WikiFolder | undefined>(undefined)
  const [q, setQ] = useState('')
  const [debounced, setDebounced] = useState('')
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 300)
    return () => clearTimeout(t)
  }, [q])

  const navigate = useNavigate()
  /** 换分类：离开正在读的那一页，回到列表。 */
  const pick = (t?: string, f?: WikiFolder) => {
    setType(t)
    setFolder(f)
    setQ('')
    if (slug) navigate(`/kb/${kbId}/wiki`)
  }
  const by = stats.data?.pages_by_type ?? {}

  return (
    <div className="kbp wiki">
      <KbHead kbId={kbId} kb={kb.data} missing={kb.isError} tab="wiki">
        {stats.data && (
          <span className="wiki-stat">
            {stats.data.total_pages} 页 · {stats.data.total_links} 条链接
          </span>
        )}
      </KbHead>

      <nav className="tray" aria-label="Wiki 分类">
        <FolderRow name="概览" current={!type && !slug} onClick={() => pick()} />
        {TYPES.map((t) => (
          <WikiTypeTree key={t} kbId={kbId} type={t} count={by[t]} current={type === t} folder={folder} onPick={(f) => pick(t, f)} />
        ))}
      </nav>

      <div className="wiki-main">
        <label className="sk-search">
          <Search width={16} height={16} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="在 Wiki 里搜索概念、实体、资料" aria-label="搜索 Wiki" />
        </label>
        {debounced ? (
          <SearchResults kbId={kbId} q={debounced} />
        ) : slug ? (
          <Reader key={slug} kbId={kbId} slug={slug} />
        ) : type ? (
          <PageList kbId={kbId} type={type} folder={folder} />
        ) : (
          <Overview kbId={kbId} onType={(t) => pick(t)} />
        )}
      </div>
    </div>
  )
}

function WikiTypeTree({ kbId, type, count, current, folder, onPick }: { kbId: string; type: string; count?: number; current: boolean; folder?: WikiFolder; onPick: (f?: WikiFolder) => void }) {
  const [open, setOpen] = useState(false)
  const roots = useQuery({ queryKey: ['wiki-folders', kbId, type, ''], enabled: open, queryFn: () => wikiFolders(kbId, '', type) })
  return (
    <>
      <FolderRow name={WIKI_TYPE_LABEL[type] ?? type} count={count} current={current && !folder} onClick={() => onPick()} expanded={open} onToggle={type === 'summary' ? undefined : () => setOpen((v) => !v)} />
      {open && roots.data?.map((f) => <WikiFolderNode key={f.id} kbId={kbId} type={type} node={f} depth={1} current={current ? folder : undefined} onPick={onPick} />)}
    </>
  )
}

function WikiFolderNode({ kbId, type, node, depth, current, onPick }: { kbId: string; type: string; node: WikiFolder; depth: number; current?: WikiFolder; onPick: (f: WikiFolder) => void }) {
  const [open, setOpen] = useState(false)
  const kids = useQuery({ queryKey: ['wiki-folders', kbId, type, node.id], enabled: open, queryFn: () => wikiFolders(kbId, node.id, type) })
  return (
    <>
      <FolderRow name={node.name} count={node.page_count} current={current?.id === node.id} onClick={() => onPick(node)} depth={depth} expanded={open} onToggle={node.has_children ? () => setOpen((v) => !v) : undefined} />
      {open && kids.data?.map((f) => <WikiFolderNode key={f.id} kbId={kbId} type={type} node={f} depth={depth + 1} current={current} onPick={onPick} />)}
    </>
  )
}

const pageHref = (kbId: string, slug: string) => `/kb/${kbId}/wiki/${slugPath(slug)}`

function PageCard({ kbId, p }: { kbId: string; p: Pick<WikiPage, 'slug' | 'title' | 'summary' | 'page_type'> & { category_path?: string[] } }) {
  return (
    <Link to={pageHref(kbId, p.slug)} className="sk-card wiki-card" data-interactive="true" aria-label={p.title.replace(/\.md - Summary$|\s-\sSummary$/, '')}>
      <span className="wiki-type" data-type={p.page_type}>
        {WIKI_TYPE_LABEL[p.page_type] ?? p.page_type}
      </span>
      <span className="sk-card-title">{p.title.replace(/\.md - Summary$|\s-\sSummary$/, '')}</span>
      {p.summary && <span className="sk-card-body">{p.summary}</span>}
      {p.category_path?.length ? <span className="wiki-cat">{p.category_path.join(' / ')}</span> : null}
    </Link>
  )
}

function Overview({ kbId, onType }: { kbId: string; onType: (t: string) => void }) {
  const idx = useQuery({ queryKey: ['wiki-index', kbId], queryFn: () => wikiIndex(kbId, 6), staleTime: 5 * 60_000 })
  if (idx.isPending) return <div className="wiki-wait" aria-busy="true" />
  if (idx.isError) return <p className="empty">没能载入 Wiki：{idx.error.message}</p>
  return (
    <div className="wiki-overview">
      {idx.data.intro && (
        <article className="answer wiki-intro">
          <WikiMarkdown kbId={kbId} md={idx.data.intro.replace(/^#\s+.*\n+/, '')} />
        </article>
      )}
      {idx.data.groups
        .filter((g) => g.type !== 'index' && g.items.length)
        .sort((a, b) => order(a.type) - order(b.type))
        .map((g) => (
          <section key={g.type} className="wiki-group" aria-label={WIKI_TYPE_LABEL[g.type] ?? g.type}>
            <div className="wiki-group-h">
              <h2>{WIKI_TYPE_LABEL[g.type] ?? g.type}</h2>
              <span>{g.total} 页</span>
              <span className="grow" />
              <Pill size="sm" onClick={() => onType(g.type)}>
                全部{WIKI_TYPE_LABEL[g.type] ?? ''}
              </Pill>
            </div>
            <div className="wiki-grid">
              {g.items.map((it) => (
                <PageCard key={it.slug} kbId={kbId} p={{ ...it, page_type: g.type }} />
              ))}
            </div>
          </section>
        ))}
    </div>
  )
}

function PageList({ kbId, type, folder }: { kbId: string; type: string; folder?: WikiFolder }) {
  const list = useInfiniteQuery({
    queryKey: ['wiki-pages', kbId, type, folder?.id ?? null],
    queryFn: ({ pageParam }) => wikiPages(kbId, { type, folderId: folder?.id, page: pageParam, pageSize: PAGE }),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.page * last.page_size < last.total ? last.page + 1 : undefined),
  })
  const items = list.data?.pages.flatMap((p) => p.pages) ?? []
  const total = list.data?.pages[0]?.total ?? 0
  return (
    <section className="wiki-group" aria-label={`${WIKI_TYPE_LABEL[type] ?? type}${folder ? `：${folder.name}` : ''}`}>
      <div className="wiki-group-h">
        <h2>
          {WIKI_TYPE_LABEL[type] ?? type}
          {folder ? ` · ${folder.path || folder.name}` : ''}
        </h2>
        {list.isSuccess && <span>{total} 页</span>}
      </div>
      {list.isError && <p className="empty">没能载入：{list.error.message}</p>}
      {list.isSuccess && !items.length && <p className="empty">这里没有页面</p>}
      <div className="wiki-grid" aria-busy={list.isPending}>
        {items.map((p) => (
          <PageCard key={p.slug} kbId={kbId} p={p} />
        ))}
      </div>
      {list.hasNextPage && (
        <div className="list-foot">
          <Pill onClick={() => list.fetchNextPage()} disabled={list.isFetchingNextPage}>
            再看 {Math.min(PAGE, total - items.length)} 页
          </Pill>
        </div>
      )}
    </section>
  )
}

function SearchResults({ kbId, q }: { kbId: string; q: string }) {
  const r = useQuery({ queryKey: ['wiki-search', kbId, q], queryFn: () => wikiSearch(kbId, q, 30) })
  return (
    <section className="wiki-group" aria-label="搜索结果">
      <div className="wiki-group-h">
        <h2>「{q}」</h2>
        {r.isSuccess && <span>{r.data.length} 页</span>}
      </div>
      {r.isError && <p className="empty">没搜成：{r.error.message}</p>}
      {r.isSuccess && !r.data.length && <p className="empty">Wiki 里没有和「{q}」相关的页面</p>}
      <div className="wiki-grid" aria-busy={r.isPending}>
        {r.data?.map((p) => (
          <PageCard key={p.slug} kbId={kbId} p={p} />
        ))}
      </div>
    </section>
  )
}

export function WikiMarkdown({ kbId, md }: { kbId: string; md: string }) {
  const components: Components = useMemo(
    () => ({
      a: ({ href, children }) => {
        if (href?.startsWith('wiki:')) return <Link to={pageHref(kbId, href.slice(5))}>{children}</Link>
        if (href && /^https?:\/\//i.test(href))
          return (
            <a href={href} target="_blank" rel="noopener noreferrer">
              {children}
            </a>
          )
        return <span>{children}</span>
      },
      img: () => null,
    }),
    [kbId],
  )
  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={components} skipHtml urlTransform={(u) => u}>
      {wikiLinks(md)}
    </ReactMarkdown>
  )
}

/** 正文里 [[slug|字]] 的「字」：链出去的那些页用它当名字，不用再一页页去取。 */
function linkLabels(md: string): Map<string, string> {
  const out = new Map<string, string>()
  for (const m of md.matchAll(/\[\[([^\]|]+?)(?:\|([^\]]+?))?\]\]/g)) if (!out.has(m[1].trim())) out.set(m[1].trim(), (m[2] ?? '').trim())
  return out
}

function Reader({ kbId, slug }: { kbId: string; slug: string }) {
  const p = useQuery({ queryKey: ['wiki-page', kbId, slug], queryFn: () => wikiPage(kbId, slug) })
  const [showIn, setShowIn] = useState(false)
  if (p.isPending) return <div className="wiki-wait" aria-busy="true" />
  if (p.isError) return <p className="empty">{p.error.message.includes('not found') || p.error.message.includes('找不到') ? '没有这一页' : `没能打开：${p.error.message}`}</p>
  const page = p.data
  const labels = linkLabels(page.content ?? '')
  const body = (page.content ?? '').replace(/^#\s+.*\n+/, '')
  return (
    <div className="wiki-read">
      <article className="wiki-article" aria-label={page.title}>
        <div className="wiki-meta">
          <span className="wiki-type" data-type={page.page_type}>
            {WIKI_TYPE_LABEL[page.page_type] ?? page.page_type}
          </span>
          {page.category_path?.length ? <span className="wiki-cat">{page.category_path.join(' / ')}</span> : null}
        </div>
        <h2 className="wiki-title">{page.title.replace(/\.md - Summary$|\s-\sSummary$/, '')}</h2>
        {page.aliases?.length ? <p className="wiki-alias">也叫：{page.aliases.join('、')}</p> : null}
        <div className="answer">
          <WikiMarkdown kbId={kbId} md={body} />
        </div>
      </article>
      <aside className="wiki-side" aria-label="关联">
        {!!page.source_refs?.length && <Sources kbId={kbId} ids={page.source_refs} />}
        {!!page.out_links?.length && (
          <section>
            <h3>
              链出去的<span>{page.out_links.length}</span>
            </h3>
            <ul>
              {page.out_links.map((s) => (
                <li key={s}>
                  <LinkTo kbId={kbId} slug={s} label={labels.get(s)} />
                </li>
              ))}
            </ul>
          </section>
        )}
        {!!page.in_links?.length && (
          <section>
            <h3>
              链到它的<span>{page.in_links.length}</span>
            </h3>
            {showIn ? (
              <InLinks kbId={kbId} slugs={page.in_links} />
            ) : (
              <Pill size="sm" onClick={() => setShowIn(true)}>
                看看是哪些页
              </Pill>
            )}
          </section>
        )}
      </aside>
    </div>
  )
}

/** 链接的名字：正文里有就用正文里的，没有就用 slug 的最后一段（拼音的也比没有强）。 */
function LinkTo({ kbId, slug, label, title }: { kbId: string; slug: string; label?: string; title?: string }) {
  const type = slug.split('/')[0]
  return (
    <Link to={pageHref(kbId, slug)} className="wiki-link">
      <span className="wiki-type" data-type={type}>
        {WIKI_TYPE_LABEL[type] ?? type}
      </span>
      {title || label || slug.split('/').pop()}
    </Link>
  )
}

function InLinks({ kbId, slugs }: { kbId: string; slugs: string[] }) {
  const shown = slugs.slice(0, 30)
  const pages = useQueries({ queries: shown.map((s) => ({ queryKey: ['wiki-page', kbId, s], queryFn: () => wikiPage(kbId, s), staleTime: 5 * 60_000 })) })
  return (
    <ul>
      {shown.map((s, i) => (
        <li key={s}>
          <LinkTo kbId={kbId} slug={s} title={pages[i].data?.title.replace(/\.md - Summary$|\s-\sSummary$/, '')} />
        </li>
      ))}
      {slugs.length > shown.length && <li className="wiki-more">还有 {slugs.length - shown.length} 页</li>}
    </ul>
  )
}

function Sources({ kbId, ids }: { kbId: string; ids: string[] }) {
  const { open } = useDocPreview()
  const docs = useQueries({ queries: ids.slice(0, 10).map((id) => ({ queryKey: ['knowledge-item', id], queryFn: () => getKnowledge(id), staleTime: 5 * 60_000 })) })
  return (
    <section>
      <h3>
        来源资料<span>{ids.length}</span>
      </h3>
      <ul>
        {ids.slice(0, 10).map((id, i) => (
          <li key={id}>
            <button type="button" className="wiki-link" onClick={() => open(id, kbId)}>
              <FileText width={13} height={13} />
              {docs[i].data?.title ?? (docs[i].isError ? '（这篇资料不在了）' : '…')}
              <ArrowUpRight width={12} height={12} className="out" />
            </button>
          </li>
        ))}
      </ul>
    </section>
  )
}
